import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ThinkingOrb } from 'thinking-orbs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  useXAxisScale,
  useYAxisScale,
} from 'recharts';

import {
  useAdminKpis,
  useAdminOverviewSnapshot,
  useActiveOverTime,
  useCohortRetention,
  useDailySignups,
} from '@/lib/api/adminHooks';
import type {
  ActiveByProduct,
  ActiveOverTimeResponse,
  DailySignupsResponse,
  ProductCohortTable,
} from '@/types/admin';

import { KpiCards } from './KpiCards';
import { SyncStatusBar } from './SyncStatusBar';

export function AdminHomeScreen() {
  const overview = useAdminOverviewSnapshot();
  // Rolling-deploy/rollback compatibility: if the unified endpoint is not
  // available, fall back to the legacy independent queries instead of making
  // the whole dashboard unavailable.
  const useLegacyQueries = overview.isError;
  const legacyKpis = useAdminKpis({ enabled: useLegacyQueries });
  const legacyCohort = useCohortRetention({
    cycle: 'monthly',
    days: 180,
    cap: 5,
    groupBy: 'label',
    enabled: useLegacyQueries,
  });
  const cohort = overview.data?.cohort_retention ?? legacyCohort.data;

  // Shared by both active-subscriber charts so they resolve to ONE query.
  // These used to disagree — the overall chart let topProducts default to 6
  // while the by-product chart passed 15 — which produced two distinct
  // React Query keys and therefore two full server-side computations of the
  // same data (37s each, measured on prod). `overall` is computed before the
  // top-N trim server-side, so it's byte-identical at any segment count.
  const [segments, setSegments] = useState<number>(15);

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">Overview</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          Headline KPIs, active-subscriber trend, and paid-renewal retention
          by product — cached from CarePortals.
        </p>
      </div>

      <SyncStatusBar />

      <KpiCards
        data={overview.data?.kpis ?? legacyKpis.data}
        isLoading={overview.isLoading || (useLegacyQueries && legacyKpis.isLoading)}
        isError={useLegacyQueries && legacyKpis.isError}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <ActiveOverallChart
          segments={segments}
          snapshotData={overview.data?.active_over_time}
          fallbackToLegacy={useLegacyQueries}
        />
        <ActiveByProductChart
          segments={segments}
          onSegmentsChange={setSegments}
          snapshotData={overview.data?.active_over_time}
          fallbackToLegacy={useLegacyQueries}
        />
      </div>

      <DailyBarChart
        metric="signups"
        snapshotData={overview.data?.daily_signups}
        fallbackToLegacy={useLegacyQueries}
      />
      <DailyBarChart
        metric="revenue"
        snapshotData={overview.data?.daily_signups}
        fallbackToLegacy={useLegacyQueries}
      />

      <section className="border border-line rounded-lg bg-raised overflow-hidden">
        <div className="p-16 border-b border-line">
          <h2 className="type-heading-md">Retention by product (monthly)</h2>
          <p className="type-label-sm text-ink-muted mt-4">
            Weighted across every cohort whose M_N window has fully closed.
            Click a product to break it down by CP variants.
          </p>
        </div>
        {cohort ? <AggregatedRetentionTable tables={cohort.tables} /> : <LoadingRow />}
      </section>
    </div>
  );
}

function ActiveOverallChart({
  segments,
  snapshotData,
  fallbackToLegacy,
}: {
  segments: number;
  snapshotData?: ActiveOverTimeResponse;
  fallbackToLegacy: boolean;
}) {
  const useSnapshot = segments === 15 && snapshotData != null;
  const query = useActiveOverTime({
    weeks: 12,
    cycle: 'all',
    topProducts: segments,
    enabled: !useSnapshot && (segments !== 15 || fallbackToLegacy),
  });
  const data = useSnapshot ? snapshotData : query.data;

  return (
    <div className="border border-line rounded-lg bg-raised p-16 flex flex-col gap-12">
      <div className="flex items-baseline justify-between gap-12">
        <h2 className="type-heading-md">Active subscribers</h2>
        {data && <DeltaBadge latest={data.overall.latest} delta={data.overall.wow_delta} />}
      </div>
      {data ? (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart
            data={data.week_ends.map((we, i) => ({
              week: format(parseISO(we), 'MMM d'),
              active: data.overall.series[i],
            }))}
            margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--line-subtle)" />
            <XAxis dataKey="week" stroke="var(--ink-muted)" fontSize={11} />
            <YAxis stroke="var(--ink-muted)" fontSize={11} />
            <Tooltip
              cursor={{ stroke: 'var(--line-default)' }}
              contentStyle={{ fontSize: 12 }}
            />
            <Line
              type="monotone"
              dataKey="active"
              stroke="var(--ink-primary)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <LoadingRow />
      )}
    </div>
  );
}

const CHART_PALETTE = [
  'var(--ink-primary)',
  '#4E4E4E',
  '#8A8A8A',
  '#2D6CDF',
  '#7E57C2',
  '#4CAF7B',
  '#7C7C7C',
];

const SEGMENT_OPTIONS = [
  { label: '6', value: 6 },
  { label: '10', value: 10 },
  { label: '15', value: 15 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
] as const;

const DAILY_WINDOWS = [
  { label: '7d', value: 7 },
  { label: '14d', value: 14 },
  { label: '28d', value: 28 },
  { label: '90d', value: 90 },
] as const;

const WEEKLY_WINDOWS = [
  { label: '2w', value: 2 },
  { label: '4w', value: 4 },
  { label: '6w', value: 6 },
  { label: '8w', value: 8 },
] as const;

const GRANULARITY_OPTIONS = [
  { label: 'Daily', value: 'daily' as const },
  { label: 'Weekly', value: 'weekly' as const },
];

type DailyMetric = 'signups' | 'revenue';
type RevenueMode = 'total' | 'new' | 'renewal';

const REVENUE_MODE_OPTIONS = [
  { label: 'Total', value: 'total' as const },
  { label: 'New', value: 'new' as const },
  { label: 'Renewal', value: 'renewal' as const },
];

const formatDollarsFromCents = (cents: number) => {
  if (!Number.isFinite(cents)) return '$0';
  const dollars = Math.round(cents / 100);
  return `$${dollars.toLocaleString('en-US')}`;
};

function DailyBarChart({
  metric,
  snapshotData,
  fallbackToLegacy,
}: {
  metric: DailyMetric;
  snapshotData?: DailySignupsResponse;
  fallbackToLegacy: boolean;
}) {
  const [granularity, setGranularity] = useState<'daily' | 'weekly'>('daily');
  const [dayWindow, setDayWindow] = useState<number>(14);
  const [weekWindow, setWeekWindow] = useState<number>(4);
  // Default to 15 segments — 6 was rolling too much real revenue into "Other".
  const [segments, setSegments] = useState<number>(15);
  // Default revenue mode is "new" so the chart starts where it always has been;
  // user can flip to total or renewal-only.
  const [revenueMode, setRevenueMode] = useState<RevenueMode>('new');

  const defaultSelection =
    granularity === 'daily' && dayWindow === 14 && segments === 15;
  const useSnapshot = defaultSelection && snapshotData != null;
  const query = useDailySignups({
    days: dayWindow,
    weeks: weekWindow,
    topProducts: segments,
    granularity,
    enabled: !useSnapshot && (!defaultSelection || fallbackToLegacy),
  });
  const data = useSnapshot ? snapshotData : query.data;

  const isWeekly = granularity === 'weekly';
  const isRevenue = metric === 'revenue';

  // Label density: day-level shows weekday+day up to 14, then MMM d. Weekly
  // shows Monday date for every bucket (never more than 8).
  const dayLabelFormat = isWeekly ? 'MMM d' : dayWindow <= 14 ? 'EEE MMM d' : 'MMM d';
  const tickInterval = isWeekly
    ? 0
    : dayWindow <= 14
      ? 0
      : dayWindow <= 28
        ? 1
        : Math.ceil(dayWindow / 15);

  const sourceSeries = !isRevenue
    ? data?.series
    : revenueMode === 'new'
      ? data?.revenue_series
      : revenueMode === 'renewal'
        ? data?.renewal_revenue_series
        : // 'total' — element-wise sum of new + renewal so colors and product
          // keys stay aligned with the other modes.
          data?.revenue_series?.map((newRow, i) => {
            const renRow = data.renewal_revenue_series?.[i];
            const merged: typeof newRow = { ...newRow };
            if (renRow) {
              for (const [k, v] of Object.entries(renRow)) {
                if (k === 'day') continue;
                if (typeof v === 'number') {
                  const prev = (merged[k] as number | undefined) ?? 0;
                  merged[k] = prev + v;
                }
              }
            }
            return merged;
          });

  const formattedSeries =
    sourceSeries?.map((row) => ({
      ...row,
      dayLabel: format(parseISO(String(row.day)), dayLabelFormat),
    })) ?? [];

  const formatBucketValue = (val: unknown) => {
    const n = typeof val === 'number' ? val : Number(val);
    if (!Number.isFinite(n) || n <= 0) return '';
    return isRevenue ? formatDollarsFromCents(n) : `${n}`;
  };

  // Per-bucket total labels rendered via Recharts v3 axis-scale hooks.
  // Putting <LabelList position="top"> on the topmost stacked Bar (the
  // previous approach) drops labels whenever that segment has value 0,
  // which becomes most days at higher segment counts. Computing the
  // top-of-stack pixel directly from the YAxis scale + total value is
  // immune to that. Must be a child of <BarChart> for the hooks to work.
  const BucketTotals = () => {
    const xScale = useXAxisScale();
    const yScale = useYAxisScale();
    if (!xScale || !yScale) return null;
    return (
      <g pointerEvents="none">
        {formattedSeries.map((row, i) => {
          const total = Number((row as { total?: number }).total ?? 0);
          const text = formatBucketValue(total);
          if (!text) return null;
          const x = xScale(row.dayLabel, { position: 'middle' });
          const y = yScale(total);
          if (x == null || y == null) return null;
          return (
            <text
              key={i}
              x={x}
              y={y - 6}
              textAnchor="middle"
              fontSize={10}
              fill="var(--ink)"
            >
              {text}
            </text>
          );
        })}
      </g>
    );
  };

  const noun = isRevenue ? 'revenue' : 'subscribers';
  // Revenue chart's leading word changes with the toggle; signups stays "new".
  const titlePrefix = isRevenue
    ? revenueMode === 'total'
      ? 'total'
      : revenueMode === 'renewal'
        ? 'renewal'
        : 'new'
    : 'new';
  const title = isWeekly
    ? `Weekly ${titlePrefix} ${noun} (${weekWindow}w)`
    : `Daily ${titlePrefix} ${noun} (${dayWindow}d)`;
  const baseDescription = isWeekly
    ? `New cp_subscriptions bucketed by ISO week (Monday-start) in`
    : `New cp_subscriptions bucketed by your local calendar day in`;
  const revenueDescription = isWeekly
    ? `revenue bucketed by ISO week (Monday-start) in`
    : `revenue bucketed by your local calendar day in`;
  const description = !isRevenue
    ? baseDescription
    : revenueMode === 'total'
      ? `Combined first-cycle + paid renewal ${revenueDescription}`
      : revenueMode === 'renewal'
        ? `Paid renewal (subscription_cycle) ${revenueDescription}`
        : `First-cycle revenue from new cp_subscriptions bucketed by ${
            isWeekly ? 'ISO week (Monday-start)' : 'your local calendar day'
          } in`;

  return (
    <section className="border border-line rounded-lg bg-raised p-16 flex flex-col gap-12">
      <div className="flex items-start justify-between gap-12 flex-wrap">
        <div>
          <h2 className="type-heading-md">{title}</h2>
          <p className="type-label-sm text-ink-muted mt-4">
            {description}
            {data?.tz ? ` ${data.tz}` : ' your timezone'}, stacked by
            product. Current {isWeekly ? 'week' : 'day'} is partial. Hover a
            bar for per-product breakdown.
          </p>
        </div>
        <div className="flex items-center gap-8 flex-wrap">
          {isRevenue && (
            <Segmented
              options={REVENUE_MODE_OPTIONS}
              value={revenueMode}
              onChange={setRevenueMode}
            />
          )}
          <Segmented
            options={GRANULARITY_OPTIONS}
            value={granularity}
            onChange={(v) => setGranularity(v)}
          />
          {isWeekly ? (
            <Segmented
              options={WEEKLY_WINDOWS}
              value={weekWindow}
              onChange={setWeekWindow}
            />
          ) : (
            <Segmented
              options={DAILY_WINDOWS}
              value={dayWindow}
              onChange={setDayWindow}
            />
          )}
          <label className="flex items-center gap-6 type-label-sm text-ink-muted">
            Segments
            <Segmented
              options={SEGMENT_OPTIONS}
              value={segments}
              onChange={setSegments}
            />
          </label>
        </div>
      </div>
      {data ? (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={formattedSeries}
              margin={{ top: 24, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line-subtle)" />
              <XAxis
                dataKey="dayLabel"
                stroke="var(--ink-muted)"
                fontSize={11}
                interval={tickInterval}
              />
              <YAxis
                stroke="var(--ink-muted)"
                fontSize={11}
                allowDecimals={false}
                tickFormatter={(v) => (isRevenue ? formatDollarsFromCents(Number(v)) : `${v}`)}
              />
              <Tooltip
                cursor={{ fill: 'var(--line-subtle)', opacity: 0.3 }}
                content={
                  <DailyBarTooltip
                    products={data.products}
                    metric={metric}
                  />
                }
              />
              {data.products.map((p, idx) => (
                <Bar
                  key={p}
                  dataKey={p}
                  stackId={metric}
                  fill={CHART_PALETTE[idx % CHART_PALETTE.length]}
                />
              ))}
              <BucketTotals />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-16 gap-y-4 type-label-sm">
            {data.products.map((p, idx) => (
              <div key={p} className="flex items-center gap-6">
                <span
                  className="inline-block w-8 h-8 rounded-full"
                  style={{ backgroundColor: CHART_PALETTE[idx % CHART_PALETTE.length] }}
                />
                <span className="text-ink">{p}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <LoadingRow />
      )}
    </section>
  );
}

function DailyBarTooltip({
  active,
  payload,
  label,
  products,
  metric,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
  products?: string[];
  metric: DailyMetric;
}) {
  if (!active || !payload?.length) return null;
  const isRevenue = metric === 'revenue';
  const formatVal = (v: number) => (isRevenue ? formatDollarsFromCents(v) : `${v}`);
  const byKey = new Map(payload.map((p) => [p.dataKey, p]));
  const ordered = (products ?? payload.map((p) => p.dataKey))
    .map((key) => byKey.get(key))
    .filter((p): p is NonNullable<typeof p> => !!p && p.value > 0)
    .sort((a, b) => b.value - a.value);
  const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
  return (
    <div className="bg-raised border border-line rounded-lg p-12 shadow-sm min-w-[180px]">
      <div className="type-label-sm font-medium text-ink mb-6">{label}</div>
      <div className="type-label-sm text-ink-muted mb-6">
        Total: <span className="text-ink font-medium">{formatVal(total)}</span>
      </div>
      <div className="flex flex-col gap-4">
        {ordered.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-8 type-label-sm">
            <span
              className="inline-block w-8 h-8 rounded-full shrink-0"
              style={{ backgroundColor: p.color }}
            />
            <span className="text-ink-muted flex-1 truncate">{p.dataKey}</span>
            <span className="text-ink font-medium">{formatVal(p.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActiveByProductChart({
  segments,
  onSegmentsChange,
  snapshotData,
  fallbackToLegacy,
}: {
  segments: number;
  onSegmentsChange: (n: number) => void;
  snapshotData?: ActiveOverTimeResponse;
  fallbackToLegacy: boolean;
}) {
  const useSnapshot = segments === 15 && snapshotData != null;
  const query = useActiveOverTime({
    weeks: 12,
    cycle: 'all',
    topProducts: segments,
    enabled: !useSnapshot && (segments !== 15 || fallbackToLegacy),
  });
  const data = useSnapshot ? snapshotData : query.data;

  const rows =
    data?.week_ends.map((we, i) => {
      const row: Record<string, number | string> = {
        week: format(parseISO(we), 'MMM d'),
      };
      for (const p of data.by_product) {
        row[p.label] = p.series[i];
      }
      return row;
    }) ?? [];

  const palette = [
    'var(--ink-primary)',
    '#4E4E4E',
    '#8A8A8A',
    '#2D6CDF',
    '#7E57C2',
    '#4CAF7B',
    '#7C7C7C',
  ];

  return (
    <div className="border border-line rounded-lg bg-raised p-16 flex flex-col gap-12">
      <div className="flex items-baseline justify-between gap-12 flex-wrap">
        <h2 className="type-heading-md">Active by product</h2>
        <label className="flex items-center gap-6 type-label-sm text-ink-muted">
          Segments
          <Segmented
            options={SEGMENT_OPTIONS}
            value={segments}
            onChange={onSegmentsChange}
          />
        </label>
      </div>
      {data ? (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line-subtle)" />
              <XAxis dataKey="week" stroke="var(--ink-muted)" fontSize={11} />
              <YAxis stroke="var(--ink-muted)" fontSize={11} />
              <Tooltip
                cursor={{ stroke: 'var(--line-default)' }}
                content={<ActiveByProductTooltip />}
              />
              {data.by_product.map((p, idx) => (
                <Line
                  key={p.label}
                  type="monotone"
                  dataKey={p.label}
                  stroke={palette[idx % palette.length]}
                  strokeWidth={1.5}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <LegendWithDeltas items={data.by_product} palette={palette} />
        </>
      ) : (
        <LoadingRow />
      )}
    </div>
  );
}

function ActiveByProductTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const ordered = [...payload]
    .filter((p) => Number.isFinite(p.value) && (p.value as number) > 0)
    .sort((a, b) => (b.value as number) - (a.value as number));
  return (
    <div className="bg-raised border border-line rounded-lg p-12 shadow-sm min-w-[180px]">
      <div className="type-label-sm font-medium text-ink mb-6">{label}</div>
      <div className="flex flex-col gap-4">
        {ordered.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-8 type-label-sm">
            <span
              className="inline-block w-8 h-8 rounded-full shrink-0"
              style={{ backgroundColor: p.color }}
            />
            <span className="text-ink-muted flex-1 truncate">{p.dataKey}</span>
            <span className="text-ink font-medium">
              {p.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LegendWithDeltas({
  items,
  palette,
}: {
  items: ActiveByProduct[];
  palette: string[];
}) {
  return (
    <div className="flex flex-wrap gap-x-16 gap-y-4 type-label-sm">
      {items.map((p, idx) => (
        <div key={p.label} className="flex items-center gap-6">
          <span
            className="inline-block w-8 h-8 rounded-full"
            style={{ backgroundColor: palette[idx % palette.length] }}
          />
          <span className="text-ink">{p.label}</span>
          <span className="text-ink-muted">{p.latest.toLocaleString()}</span>
          <span
            className={
              p.wow_delta > 0
                ? 'text-success'
                : p.wow_delta < 0
                  ? 'text-danger'
                  : 'text-ink-muted'
            }
          >
            {p.wow_delta > 0 ? '+' : ''}
            {p.wow_delta}
          </span>
        </div>
      ))}
    </div>
  );
}

function DeltaBadge({ latest, delta }: { latest: number; delta: number }) {
  const color =
    delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-ink-muted';
  return (
    <span className="type-label-sm text-ink-muted">
      {latest.toLocaleString()}{' '}
      <span className={color}>
        ({delta > 0 ? '+' : ''}
        {delta} WoW)
      </span>
    </span>
  );
}

function AggregatedRetentionTable({ tables }: { tables: ProductCohortTable[] }) {
  const cap = tables[0]?.weighted.length ?? 0;
  const rows = tables
    .filter((t) => t.weighted.some((c) => c.observable))
    .map((t) => ({
      product_id: t.product_id,
      label: t.product_label.trim(),
      n_total: t.n_total,
      weighted: t.weighted,
    }))
    .sort((a, b) => b.n_total - a.n_total);

  if (rows.length === 0) {
    return (
      <div className="p-16 type-label-md text-ink-muted">
        No cohorts old enough to compute retention yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full type-label-sm">
        <thead className="bg-sunken border-b border-line text-left text-ink-muted">
          <tr>
            <Th>Product</Th>
            <Th align="right">N</Th>
            {Array.from({ length: cap }).map((_, i) => (
              <Th key={i} align="center">
                M{i}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.product_id} className="border-t border-line-subtle">
              <Td>
                <Link
                  to={`/admin/products/${encodeURIComponent(row.label)}`}
                  className="underline decoration-line underline-offset-4 hover:decoration-ink"
                >
                  {row.label}
                </Link>
              </Td>
              <Td align="right">{row.n_total.toLocaleString()}</Td>
              {row.weighted.map((cell, i) => (
                <Td key={i} align="center" style={cellStyle(cell.pct, cell.observable)}>
                  {cell.observable && cell.pct != null ? `${Math.round(cell.pct)}%` : '—'}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Segmented<T>({
  options,
  value,
  onChange,
}: {
  options: readonly { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex rounded border border-line overflow-hidden type-label-sm">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-10 py-4 ${
            value === o.value
              ? 'bg-ink text-ink-onAccent'
              : 'bg-raised hover:bg-sunken text-ink-muted'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function LoadingRow() {
  // Admin surface is light-only; `theme` is pinned because the package's
  // `auto` follows the OS scheme — a dark-mode OS would paint light ink on
  // our white cards (invisible orb).
  return (
    <div
      role="status"
      aria-label="Loading"
      className="flex items-center justify-center py-32"
    >
      <ThinkingOrb state="solving" size={64} theme="light" />
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
}) {
  return (
    <th
      className={`px-12 py-8 font-medium ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  style,
}: {
  children?: React.ReactNode;
  align?: 'right' | 'center';
  style?: React.CSSProperties;
}) {
  return (
    <td
      className={`px-12 py-8 ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
      }`}
      style={style}
    >
      {children}
    </td>
  );
}

function cellStyle(pct: number | null, observable: boolean): React.CSSProperties {
  if (!observable || pct == null) {
    return { color: 'var(--ink-muted)' };
  }
  const intensity = Math.max(0, Math.min(100, pct)) / 100;
  const alpha = 0.08 + intensity * 0.5;
  return {
    backgroundColor: `rgba(0, 0, 0, ${alpha})`,
    color: intensity > 0.55 ? '#fff' : undefined,
  };
}
